import { useCallback, useEffect, useMemo, useState } from 'react';
import { Popup } from 'react-leaflet';
import type * as L from 'leaflet';
import {
  listTenantParcels,
  listLinkedParcelRefs,
  listParcelHazards,
  listParcelMemberStatus,
  getParcelCacheStatus,
} from '@/api/parcels';
import { idbGet, idbSet } from '@/utils/idbCache';
import {
  getParcelRef,
  formatParcelLabel,
  type ParcelRecord,
  type ParcelStatus,
} from '@/utils/parcelGeometry';
import { ParcelVectorTiles } from '@/components/maps/ParcelVectorTiles';

interface ParcelOverlayProps {
  /** Tenant whose parcels to fetch. Renders nothing when null. */
  tenantId: number | null;
  /** Toggle on/off via MapSettings. */
  enabled: boolean;
  /** When true, fetches once and caches in-memory + IndexedDB for the session. */
  cache?: boolean;
  /** Show parcel labels (ParcelNo) — size-LOD gated in the tile renderer. */
  labelZoom?: number;
  /** Independent of zoom: when false the parcel-number label is suppressed. */
  showLabels?: boolean;

  /**
   * Hazard-coloured labels: ref → hazard tags. When a parcel's ref is
   * present, its number is drawn on a worst-wins hazard-coloured chip.
   *
   * Omit it and the overlay fetches its own from the identity-free hazards
   * endpoint, so any map showing parcels gets hazard labels without per-page
   * wiring. Pass a map to override — the member-management pages already hold
   * full member records and build theirs from those.
   */
  hazardByRef?: ReadonlyMap<string, string[]> | null;
  /**
   * ref → membership/payment status, driving the parcel colour ramp.
   *
   * Same contract as `hazardByRef`: omit it and the overlay fetches its own
   * from the identity-free member-status endpoint, so every map showing
   * parcels gets the ramp without per-page wiring. Pass a map to override.
   */
  statusByRef?: ReadonlyMap<string, ParcelStatus> | null;
  /** A single parcel ref to highlight with the purple outline style. */
  highlightRef?: string | null;
  /**
   * Restrict rendering to parcels in these boundary slugs (client-side
   * per-boundary show/hide). `null`/omitted renders every fetched parcel.
   */
  visibleBoundaryNames?: ReadonlySet<string> | null;
  /**
   * Click handling. When provided, the overlay forwards the clicked parcel to
   * the page (which renders its own rich UI, e.g. a detail drawer) and shows
   * no built-in popup. When omitted, the overlay shows a minimal default popup.
   */
  onParcelSelect?: (parcel: ParcelRecord, latlng: L.LatLng) => void;
  /** Shares the loaded linked-ref set with the page (avoids a double fetch). */
  onLinkedRefsLoaded?: (refs: Set<string>) => void;
  /** Receives the total fetched parcel count (for page-level summaries). */
  onParcelsLoaded?: (count: number) => void;
  /**
   * Receives the full fetched parcel array itself (not just the count) —
   * used by `MapSearchControl` to build its address index off the exact
   * tenant-scoped set this overlay is rendering, without a second fetch.
   */
  onParcelsData?: (parcels: ParcelRecord[]) => void;
  /** Bump to re-fetch the link set (e.g. after a link/unlink). */
  refreshKey?: number;
  /**
   * When false, parcels render but are non-interactive — no click hit-test,
   * popup, or selection. Used while drawing (so clicks plot a polygon) and
   * anywhere parcels are context-only. Defaults to true.
   */
  interactive?: boolean;
}

interface LinkedRefsResponse {
  linked_refs?: string[];
}

interface HazardsResponse {
  hazards?: Array<{ parcel_ref: string; hazard_tags: string[] }>;
}

interface MemberStatusResponse {
  statuses?: Array<{ parcel_ref: string; status: ParcelStatus }>;
}

interface ParcelsResponse {
  parcels?: ParcelRecord[];
}

// In-memory cache keyed by `${tenantId}:${version}` — survives toggle on/off
// within a session. IndexedDB carries the same blob across sessions; both are
// invalidated only when the server reports a newer version (fetch-once,
// refetch-on-version-change contract).
const memCache = new Map<string, ParcelRecord[]>();

/**
 * Every parcel this overlay currently has loaded, across tenants/versions.
 *
 * Lets a sibling layer answer "which parcel is under this point?" using the
 * exact geometry that was drawn, without a second fetch or a prop threaded
 * through MapCanvas. Empty until the overlay has run, which is the intended
 * behaviour: a point lookup is only offered while parcels are actually shown.
 */
export function loadedParcels(): ParcelRecord[] {
  const out: ParcelRecord[] = [];
  for (const list of memCache.values()) out.push(...list);
  return out;
}

/**
 * Renders an FPA's parcels via canvas vector tiles (see `ParcelVectorTiles`).
 * Colour is a single-hue ramp over membership and current-year payment state —
 * unlinked, non-member, member-unpaid, member-paid (see PARCEL_STYLE_BY_STATUS).
 * Geometry is cached per-tenant under a version key derived from the latest
 * reindex timestamp; the status set is fetched independently so cached geometry
 * paints immediately and re-colours when statuses arrive.
 *
 * Behaviour is prop-driven so one renderer serves every map: hazard labels,
 * per-boundary visibility, interactivity, and page-owned selection are all
 * opt-in. Auth: the endpoint requires PARCEL/LIST, so MapSettings disables the
 * parcels toggle for non-staff.
 */
export function ParcelOverlay({
  tenantId,
  enabled,
  cache: useCache = true,
  labelZoom = 16,
  showLabels: showLabelsProp = true,
  hazardByRef = null,
  statusByRef = null,
  highlightRef = null,
  visibleBoundaryNames = null,
  onParcelSelect,
  onLinkedRefsLoaded,
  onParcelsLoaded,
  onParcelsData,
  refreshKey = 0,
  interactive = true,
}: ParcelOverlayProps) {
  const [parcels, setParcels] = useState<ParcelRecord[]>([]);
  const [linkedRefs, setLinkedRefs] = useState<Set<string>>(new Set());
  const [fetchedHazards, setFetchedHazards] = useState<ReadonlyMap<string, string[]> | null>(null);
  const [fetchedStatuses, setFetchedStatuses] = useState<ReadonlyMap<string, ParcelStatus> | null>(
    null,
  );
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState<{ parcel: ParcelRecord; latlng: L.LatLng } | null>(null);

  // A caller-supplied map wins; otherwise use whatever we fetched ourselves.
  const effectiveHazardByRef = hazardByRef ?? fetchedHazards;
  const effectiveStatusByRef = statusByRef ?? fetchedStatuses;

  useEffect(() => {
    if (loaded) {
      onParcelsLoaded?.(parcels.length);
      onParcelsData?.(parcels);
    }
    // onParcelsLoaded/onParcelsData identity may change per render; only fire
    // on data change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, parcels]);

  // Links fetch — independent of geometry, and re-runnable via refreshKey.
  useEffect(() => {
    if (!enabled || tenantId == null) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const linkedData = (await listLinkedParcelRefs(tenantId)) as LinkedRefsResponse;
        if (cancelled) return;
        const refs = new Set(linkedData.linked_refs ?? []);
        setLinkedRefs(refs);
        onLinkedRefsLoaded?.(refs);
      } catch (err) {
        console.warn('Failed to load parcel links:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // onLinkedRefsLoaded intentionally excluded (parent may recreate identity).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, enabled, refreshKey]);

  // Hazards fetch — skipped when the page supplies its own map, or when labels
  // are off (they're the only thing hazard colours feed, and the join costs an
  // extra member-repo query server-side). Failure is non-fatal: labels just
  // fall back to the neutral chip.
  useEffect(() => {
    if (!enabled || tenantId == null || hazardByRef || !showLabelsProp) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = (await listParcelHazards(tenantId)) as HazardsResponse;
        if (cancelled) return;
        setFetchedHazards(
          new Map((data.hazards ?? []).map((h) => [h.parcel_ref, h.hazard_tags])),
        );
      } catch (err) {
        console.warn('Failed to load parcel hazards:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, enabled, refreshKey, hazardByRef, showLabelsProp]);

  // Member/payment status fetch — feeds the colour ramp. Unlike hazards this
  // is NOT gated on labels: colouring is needed whenever parcels are drawn.
  // Refetched on refreshKey so a link/unlink recolours without a reload.
  // Failure is non-fatal — parcels just stay in the neutral unlinked grey.
  useEffect(() => {
    if (!enabled || tenantId == null || statusByRef) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = (await listParcelMemberStatus(tenantId)) as MemberStatusResponse;
        if (cancelled) return;
        setFetchedStatuses(
          new Map((data.statuses ?? []).map((s) => [s.parcel_ref, s.status])),
        );
      } catch (err) {
        console.warn('Failed to load parcel member status:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, enabled, refreshKey, statusByRef]);

  // Geometry fetch — cache-version gated: probe version, serve cache, else fetch.
  useEffect(() => {
    if (!enabled || tenantId == null) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const status = await getParcelCacheStatus();
        const version = status?.version ?? 'none';
        const memKey = `${tenantId}:${version}`;
        const idbKey = `tenant:${tenantId}:v:${version}`;

        const fromMem = useCache ? memCache.get(memKey) : undefined;
        if (fromMem) {
          if (cancelled) return;
          setParcels(fromMem);
          setLoaded(true);
          return;
        }
        if (useCache) {
          const fromIdb = await idbGet('parcels', idbKey);
          if (fromIdb && Array.isArray(fromIdb)) {
            if (cancelled) return;
            memCache.set(memKey, fromIdb);
            setParcels(fromIdb);
            setLoaded(true);
            return;
          }
        }
        const parcelsData = (await listTenantParcels(tenantId, {
          include_geometry: true,
        })) as ParcelsResponse;
        if (cancelled) return;
        const list = parcelsData.parcels ?? [];
        setParcels(list);
        setLoaded(true);
        if (useCache) {
          memCache.set(memKey, list);
          idbSet('parcels', idbKey, list).catch(() => {});
        }
      } catch (err) {
        console.warn('Failed to load parcels overlay:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, enabled, useCache]);

  // Client-side per-boundary visibility filter.
  const visibleParcels = useMemo(() => {
    if (!visibleBoundaryNames) return parcels;
    return parcels.filter(
      (p) => p.boundary_name != null && visibleBoundaryNames.has(p.boundary_name),
    );
  }, [parcels, visibleBoundaryNames]);

  const handleParcelClick = useCallback(
    (parcel: ParcelRecord, latlng: L.LatLng) => {
      if (onParcelSelect) onParcelSelect(parcel, latlng);
      else setSelected({ parcel, latlng });
    },
    [onParcelSelect],
  );

  if (!enabled || !loaded || visibleParcels.length === 0) return null;

  const selectedRef = selected ? getParcelRef(selected.parcel) : null;

  return (
    <>
      <ParcelVectorTiles
        parcels={visibleParcels}
        statusByRef={effectiveStatusByRef}
        highlightRef={highlightRef}
        hazardByRef={effectiveHazardByRef}
        showLabels={showLabelsProp}
        labelZoom={labelZoom}
        interactive={interactive}
        onParcelClick={interactive ? handleParcelClick : undefined}
      />
      {/* Default minimal popup — only when the page hasn't opted into its own
          selection UI via onParcelSelect. */}
      {!onParcelSelect && selected && (
        <Popup position={selected.latlng} eventHandlers={{ remove: () => setSelected(null) }}>
          <div className="text-xs">
            <div className="font-semibold">
              {formatParcelLabel(selected.parcel) || selectedRef || 'Parcel'}
            </div>
            {selectedRef && <div>Ref: {selectedRef}</div>}
            <div>{selectedRef && linkedRefs.has(selectedRef) ? 'Linked to FPA member' : 'Unlinked'}</div>
          </div>
        </Popup>
      )}
    </>
  );
}

export default ParcelOverlay;
